import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useGetReleaseLatestQuery } from '@/generated/graphql';

type PlatformKey = 'mac' | 'win' | 'linux';

interface PlatformOption {
  key: PlatformKey;
  label: string;
  url: string;
}

const normalizeReleaseChannel = (value?: string): 'dev' | 'stable' => {
  return value?.toLowerCase() === 'stable' ? 'stable' : 'dev';
};

export const DownloadDesktopAppPanel: React.FC = () => {
  const [platform, setPlatform] = useState<string>('unknown');

  // Release channel is controlled only by ELECTRON_RELEASE_CHANNEL (dev or stable).
  const releaseChannel = normalizeReleaseChannel(
    import.meta.env.ELECTRON_RELEASE_CHANNEL as string | undefined
  );

  const { data, loading, error } = useGetReleaseLatestQuery({
    variables: { channel: releaseChannel },
  });

  useEffect(() => {
    const detectPlatform = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      if (userAgent.includes('win')) return 'win';
      if (userAgent.includes('mac')) return 'mac';
      if (userAgent.includes('linux')) return 'linux';
      return 'unknown';
    };

    const detectedPlatform = detectPlatform();
    setPlatform(detectedPlatform);
  }, []);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <CircularProgress size={24} />
        <Typography>Loading download information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load download information: {error.message}
      </Alert>
    );
  }

  if (!data?.releaseLatest) {
    return <Alert severity="warning">No release information available</Alert>;
  }

  const platformOptions: PlatformOption[] = [
    data.releaseLatest.urls.mac
      ? { key: 'mac', label: 'macOS', url: data.releaseLatest.urls.mac }
      : null,
    data.releaseLatest.urls.win
      ? { key: 'win', label: 'Windows', url: data.releaseLatest.urls.win }
      : null,
    data.releaseLatest.urls.linux
      ? { key: 'linux', label: 'Linux', url: data.releaseLatest.urls.linux }
      : null,
  ].filter((item): item is PlatformOption => item !== null);

  if (platformOptions.length === 0) {
    return (
      <Alert severity="warning">
        No release binaries are currently available.
      </Alert>
    );
  }

  const detectedPlatformKey = ['mac', 'win', 'linux'].includes(platform)
    ? (platform as PlatformKey)
    : null;

  const primaryOption =
    (detectedPlatformKey
      ? platformOptions.find((option) => option.key === detectedPlatformKey)
      : undefined) ?? platformOptions[0];

  const backupOptions = platformOptions.filter(
    (option) => option.key !== primaryOption.key
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2.5} alignItems="center">
        <Button
          href={primaryOption.url}
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          size="large"
          sx={{
            px: 3,
            py: 1.25,
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: { xs: '100%', sm: 320 },
          }}
        >
          Download for {primaryOption.label}
        </Button>

        <Box sx={{ width: '100%' }}>
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'text.secondary',
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            All Platforms
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {platformOptions.map((option) => (
              <Button
                key={option.key}
                href={option.url}
                variant={
                  option.key === primaryOption.key ? 'contained' : 'neutral'
                }
                size="small"
                startIcon={<DownloadIcon />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                {option.label}
                {option.key === primaryOption.key ? ' (Detected)' : ''}
              </Button>
            ))}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ textAlign: 'center' }}
        >
          Version {data.releaseLatest.version} ({data.releaseLatest.channel}{' '}
          channel)
          {backupOptions.length > 0
            ? ` • ${backupOptions.length} backup option(s)`
            : ''}
        </Typography>
      </Stack>
    </Box>
  );
};
